import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BDMatrixEliminationCategory } from '@models/bdMatrixEliminationCategory.model';
import { EliminationCriteria } from '@models/eliminationCriteria.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import {
  EliminationMatrixColumnDefinition,
  SectionType,
  PredictionEngineOptions,
  ELIMINATION_CRITERIA_OTHER_INDEX,
} from '../../mystudies/_study_constants';
import { ConfigService } from '../../services/config.service';

export interface PredictionEngineBulkFeedback {
  isFunctionPredictionMarkedAsComparable: boolean;
  isProductPredictionMarkedAsComparable: boolean;
}

@Component({
  selector: 'app-bulk-reject',
  templateUrl: './bulk-reject-dialog.component.html',
  styleUrls: ['./bulk-reject-dialog.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class BulkRejectDialogComponent implements OnInit {
  results: any[] = [];
  selectedCols: any[];
  eliminationCriteriaColumns: BDMatrixEliminationCategory[];
  predefinedColumnList: BDMatrixEliminationCategory[];
  eliminationMatrixColumnDefination = EliminationMatrixColumnDefinition;
  eliminationCriteriaTempResults: EliminationCriteria[] = [];
  reason: string;
  sectionType: SectionType;
  showValidationMessage = false;
  validationMessage: string;
  isRejectionReasonRequired: boolean;
  predictionEngineBulkFeedback: PredictionEngineBulkFeedback = {
    isFunctionPredictionMarkedAsComparable: null,
    isProductPredictionMarkedAsComparable: null,
  };
  predictionEngineOptions = PredictionEngineOptions;

  invalidFunctionPredictionDecision = false;
  invalidProductPredictionDecision = false;

  comparableFunctionDisplayOrderNumber = 9;
  comparableProductDisplayOrderNumber = 10;

  public predictionSectionVisible = false;
  public isBulkAccept = false;
  public showMixedPredictionsWarning = false;
  public showNaPredictionInBatchMessage = false;
  public comparablesWithNoPredictionsOnly = false;
  public readOnly = false;

  constructor(
    private activeModal: NgbActiveModal,
    private configService: ConfigService,
  ) {}

  async ngOnInit() {
    this.columnDefinition();
    this.isRejectionReasonRequired = await firstValueFrom(this.configService.getConfigValueBoolean(ConfigService.IS_REJECTION_REASON_REQUIRED));
  }

  columnDefinition() {
    let columnList = [];

    if (this.sectionType === SectionType.FinancialReviewMatrix) {
      const insufficentInformationCol = this.eliminationCriteriaColumns.find((c) => c.displayName === 'Insufficient information');
      const financialReviewCol =
        this.eliminationCriteriaColumns.find((c) => c.displayName === 'Financial Review') || new BDMatrixEliminationCategory();

      columnList = [
        {
          name: insufficentInformationCol.displayName,
          label: 4,
          displayOrder: 9,
          visibility: true,
          frozen: true,
          sortable: false,
          id: insufficentInformationCol.bdMatrixEliminationCategoryId,
          width: '50px',
          excelColName: '167',
        },
        {
          name: financialReviewCol.displayName,
          label: 6,
          displayOrder: 10,
          visibility: true,
          frozen: true,
          sortable: false,
          id: financialReviewCol.bdMatrixEliminationCategoryId,
          width: '50px',
          excelColName: '168',
        },
      ];
    } else {
      // Set display order for elimination Criteria column starts with 9 (may change as per column add/remove)
      let displayOrder = 9;

      this.eliminationCriteriaColumns.forEach((val) => {
        columnList.push({
          name: val.displayName,
          label: val.displayOrder.toString(),
          displayOrder: displayOrder,
          visibility: true,
          sortable: false,
          id: val.bdMatrixEliminationCategoryId,
          width: '50px',
          excelColName: val.bdMatrixEliminationCategoryId.toString(),
        });

        displayOrder = displayOrder + 1;
      });
    }

    this.selectedCols = columnList
      .filter((a) => a.visibility === true)
      .sort(function (a, b) {
        return a.displayOrder - b.displayOrder;
      });
  }

  async saveEliminationCriteria(column) {
    this.showValidationMessage = false;
    if (column) {
      this.updateEliminationCriteriaResults(column.id, column.isSelected);
      if (column.displayOrder === this.comparableFunctionDisplayOrderNumber) {
        this.predictionEngineBulkFeedback.isFunctionPredictionMarkedAsComparable =
          !this.predictionEngineBulkFeedback.isFunctionPredictionMarkedAsComparable;
        this.onFunctionDecisionTypeChanged(column.id);
      } else if (column.displayOrder === this.comparableProductDisplayOrderNumber) {
        this.predictionEngineBulkFeedback.isProductPredictionMarkedAsComparable =
          !this.predictionEngineBulkFeedback.isProductPredictionMarkedAsComparable;
        this.onProductDecisionTypeChanged(column.id);
      }
    }
  }

  async close() {
    this.activeModal.close();
  }

  private validateSave(): boolean {
    let isReasonEmpty = false;
    let isCriteriaSelected = true;
    this.showValidationMessage = false;

    const otherEliminationCategoryId = this.eliminationCriteriaColumns[ELIMINATION_CRITERIA_OTHER_INDEX].bdMatrixEliminationCategoryId;
    const otherEliminationCriteria = this.eliminationCriteriaTempResults.find(
      (ec) => ec.bdMatrixEliminationCategoryId === otherEliminationCategoryId,
    );
    const otherEliminationCriteriaSelected = _.isEmpty(otherEliminationCriteria) ? false : otherEliminationCriteria.isSelected;

    if (_.isEmpty(this.reason)) {
      isReasonEmpty = true;
    }
    const item = this.eliminationCriteriaTempResults.find((r) => r.isSelected === true);
    if (_.isEmpty(item)) {
      isCriteriaSelected = false;
    }

    if (otherEliminationCriteriaSelected && isReasonEmpty) {
      this.showValidationMessage = true;
      this.validationMessage = 'Please enter Rejection Reason.';
    }

    if (this.isRejectionReasonRequired && isReasonEmpty && !isCriteriaSelected) {
      this.showValidationMessage = true;
      this.validationMessage = 'Please enter Rejection Reason & select at least one Elimination Criteria.';
    } else if (this.isRejectionReasonRequired && isReasonEmpty) {
      this.showValidationMessage = true;
      this.validationMessage = 'Please enter Rejection Reason.';
    } else if (!isCriteriaSelected) {
      this.showValidationMessage = true;
      this.validationMessage = 'Please select at least one Elimination Criteria.';
    }

    if (this.showValidationMessage) {
      return false;
    } else {
      return true;
    }
  }

  private isPredictionFeedbackValid(): boolean {
    if (this.comparablesWithNoPredictionsOnly) return true;

    const isValid =
      this.predictionEngineBulkFeedback.isProductPredictionMarkedAsComparable !== null &&
      this.predictionEngineBulkFeedback.isFunctionPredictionMarkedAsComparable !== null;

    if (!isValid) {
      this.showValidationMessage = true;
      this.validationMessage = 'Please provide prediction feedback';
    }

    this.invalidFunctionPredictionDecision = this.predictionEngineBulkFeedback.isFunctionPredictionMarkedAsComparable === null;
    this.invalidProductPredictionDecision = this.predictionEngineBulkFeedback.isProductPredictionMarkedAsComparable === null;

    return isValid;
  }

  async save() {
    if (!this.isBulkAccept) {
      if (!this.validateSave()) {
        return false;
      }
    }

    // add elimination criteria-dialog which is not clicked by user
    for (const category of this.selectedCols) {
      const item = this.eliminationCriteriaTempResults.find((r) => r.bdMatrixEliminationCategoryId === category.id);
      if (!item) {
        const eliminationCriteriaDTO = new EliminationCriteria();
        eliminationCriteriaDTO.bdMatrixEliminationCategoryId = category.id;
        eliminationCriteriaDTO.isSelected = false;
        eliminationCriteriaDTO.sectionType = this.sectionType;
        this.eliminationCriteriaTempResults.push(eliminationCriteriaDTO);
      }
    }

    const resultSet = {
      EliminationCriteriaSelectionResults: this.eliminationCriteriaTempResults,
      reason: this.reason,
      predictionEngineBulkFeedback: this.predictionEngineBulkFeedback,
    };
    this.activeModal.close(resultSet);
    return true;
  }

  onFunctionDecisionTypeChanged(columnId): void {
    this.invalidFunctionPredictionDecision = false;

    const isSelected = this.predictionEngineBulkFeedback.isFunctionPredictionMarkedAsComparable;

    this.updateEliminationCriteriaResults(columnId, isSelected);
    this.updateDecisionCriteriaSelection(columnId, isSelected);
  }

  onProductDecisionTypeChanged(columnId): void {
    this.invalidProductPredictionDecision = false;

    const isSelected = this.predictionEngineBulkFeedback.isProductPredictionMarkedAsComparable;

    this.updateEliminationCriteriaResults(columnId, isSelected);
    this.updateDecisionCriteriaSelection(columnId, isSelected);
  }

  updateEliminationCriteriaResults(columnId: number, isSelected: boolean) {
    const item = this.eliminationCriteriaTempResults.find((r) => r.bdMatrixEliminationCategoryId === columnId);

    if (this.eliminationCriteriaTempResults.length <= 0 || !item) {
      const eliminationCriteriaDTO = new EliminationCriteria();
      eliminationCriteriaDTO.bdMatrixEliminationCategoryId = columnId;
      eliminationCriteriaDTO.isSelected = isSelected;
      eliminationCriteriaDTO.sectionType = this.sectionType;
      this.eliminationCriteriaTempResults.push(eliminationCriteriaDTO);
    } else {
      item.bdMatrixEliminationCategoryId = columnId;
      item.isSelected = isSelected;
    }
  }

  updateDecisionCriteriaSelection(columnId: number, isSelected: boolean) {
    const criteriaDecisionIndex = _.findIndex(this.selectedCols, ['id', columnId]);
    this.selectedCols[criteriaDecisionIndex].isSelected = isSelected;
  }

  showMixedPredictionsWarningMsg(): boolean {
    return this.showMixedPredictionsWarning && !this.comparablesWithNoPredictionsOnly;
  }

  showNaPredictionInBatchMessageMsgForAccept(): boolean {
    return this.showNaPredictionInBatchMessage && this.isBulkAccept && !this.comparablesWithNoPredictionsOnly;
  }

  showNaPredictionInBatchMessageMsgForReject(): boolean {
    return this.showNaPredictionInBatchMessage && !this.isBulkAccept && !this.comparablesWithNoPredictionsOnly;
  }

  comparablesWithNoPredictionsOnlyMsg(): boolean {
    return this.comparablesWithNoPredictionsOnly;
  }

  disableAIMLUserDecisionOption(rowData): boolean {
    return (
      this.readOnly ||
      (this.comparablesWithNoPredictionsOnly &&
        (rowData.displayOrder === this.comparableFunctionDisplayOrderNumber || rowData.displayOrder === this.comparableProductDisplayOrderNumber))
    );
  }
}
